import React, { useContext, useEffect, useState } from 'react';
import { useInfiniteHits } from 'react-instantsearch';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import Headline from '@components/atoms/headline/headline';
import { getSpace, getColor, mq } from '@stylesheets/tools';

import { Context } from '../../../context/provider';
import ArrowStraightSmall from '../../../../../icons/arrowStraightSmall.svg';
import ErrorItem from './ErrorItem';
import ContentItem from './ContentItem';

const StyledList = styled.div`
  ul {
    padding: ${getSpace('xs')};
    padding-bottom: 0;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    gap: ${getSpace('s')};
    margin: 0;

    ${mq('m')} {
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  .list__headline {
    border-bottom: 2px solid ${getColor('black')};
    width: calc(100% - -2px);
    position: sticky;
    top: ${getSpace('xxl')};
    z-index: 2;
    margin-left: -1px;
    padding: ${getSpace('s')} 0;
    background: ${getColor('white')};

    ${mq('m')} {
      padding: 32px 0;
    }

    ${mq('l')} {
      padding: ${getSpace('s')} 0;
    }
  }

  .list__blend {
    position: sticky;
    top: 0;
    background: ${getColor('white')};
    width: 100%;
    height: ${getSpace('xxl')};
    z-index: 3;
  }

  .list__load-more {
    text-align: center;
    padding-top: ${getSpace('m')};
  }

  .list__load-more-subline {
    font-size: 16px;
    color: ${getColor('black')};
  }

  .list__load-more-icon {
    color: ${getColor('black')};
  }
`;

const List = () => {
  const { getQueryParams } = useContext(Context);
  const { hits, isLastPage, showMore } = useInfiniteHits();
  const t = useTranslations();

  const createListsFromHits = () =>
    hits.reduce((acc, hit) => {
      const hitDate = new Date(hit.post_date_timestamp * 1000);

      // check if hitDate day, year and month is the same as today
      const isToday = hitDate.toDateString() === new Date().toDateString();
      const today = t('archive.today');

      if (isToday) {
        acc[today] = [...(acc?.[today] || []), hit];
      } else if (acc[hitDate.getFullYear()]) {
        acc[hitDate.getFullYear()] = [...acc[hitDate.getFullYear()], hit];
      } else {
        acc[hitDate.getFullYear()] = [hit];
      }

      return acc;
    }, {});

  const [hitsByDate, setHitsByDate] = useState(createListsFromHits());

  useEffect(() => {
    setHitsByDate(createListsFromHits());
  }, [hits]);

  return (
    <StyledList>
      <div className="list__blend" />

      {hits?.length === 0 && (
        <motion.ul
          initial={{
            opactiy: 0,
            y: 100
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          exit={{
            opacity: 0,
            y: 100
          }}
          transition={{ duration: 0.2, ease: 'linear' }}
          className="list"
        >
          <Headline className="list__headline" tag="h2" teaser>
            {t('archive.today')}
          </Headline>
          <li>
            <ErrorItem />
          </li>
        </motion.ul>
      )}

      {Object.entries(hitsByDate)
        ?.reverse()
        ?.map(([year, yearHits], index) => (
          <motion.ul
            initial={{
              opactiy: 0,
              y: 100
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            exit={{
              opacity: 0,
              y: 100
            }}
            transition={{ duration: 0.2, ease: 'linear' }}
            key={`${year}-${yearHits.length}-${yearHits[0].objectID || 0}`}
            className="list"
          >
            <Headline className="list__headline" tag="h2" teaser>
              {year}
            </Headline>

            {yearHits?.map(hit => {
              if (!hit.image && !hit.headline && !hit.introCopy && !hit.categories) return null;

              return (
                <li key={hit.objectID}>
                  <ContentItem
                    href={`${hit.uri}?${getQueryParams()}`}
                    image={hit.image}
                    headline={hit.headline ?? hit.title}
                    categories={hit.categories}
                    introCopy={hit.introCopy}
                  />
                </li>
              );
            })}
          </motion.ul>
        ))}

      {!isLastPage && (
        <div className="list__load-more">
          <button type="button" onClick={showMore}>
            <Headline className="list__load-more-subline" tag="span" subline>
              {t('archive.listViewShowMoreButton')}
            </Headline>
            <ArrowStraightSmall className="list__load-more-icon" width="25" height="25" />
          </button>
        </div>
      )}
    </StyledList>
  );
};

export default List;
